<script setup lang="ts">
type Props = {
  item: AccountMenu.Item;
};

const { item } = defineProps<Props>();

type Emits = {
  handleMenuItemClick: [AccountMenu.Item];
};

const emit = defineEmits<Emits>();

const route = useRoute();
const { currentRouteName } = useRoutes();

const itemIsActive = computed<boolean>(() => {
  if (item.activeWhen) {
    if (currentRouteName.value !== item.activeWhen.routeName) return false;

    const q = item.activeWhen.routeQuery as Record<string, string> | undefined;

    if (q && Object.keys(q).length > 0) {
      return Object.entries(q).every(([key, val]) => route.query[key] === val);
    }

    return !route.query.section;
  }

  return currentRouteName.value === item.routeName;
});

const customClass = computed<string>(() => {
  if (item.customClass) {
    return item.customClass;
  }

  return '';
});

const pillIsIcon = computed<boolean>(() => {
  return !!item.pill?.toString().startsWith('icon-');
});

const pillIcon = computed<string>(() => {
  return item.pill?.toString().replace('icon-', '') || '';
});
</script>

<template>
  <NuxtLink
    v-if="item.routeName"
    :class="[
      $style.menuItem,
      customClass ? $style[customClass] : '',
      { [$style.isActive]: itemIsActive },
    ]"
    :to="{
      name: item.routeName,
      query: item.routeQuery,
    }"
  >
    <NuxtIcon
      :class="$style.itemIcon"
      :name="item.icon"
    />
    <span :class="$style.itemText">
      {{ item.text }}
    </span>
    <span
      v-if="item.pill || item.pill === 0"
      :class="[$style.itemPill, item.pillClass]"
    >
      <NuxtIcon
        v-if="pillIsIcon"
        :name="pillIcon"
      />
      <span v-else>
        {{ item.pill }}
      </span>
    </span>
    <NuxtIcon
      :class="$style.itemArrow"
      name="arrows/angle_right"
    />
  </NuxtLink>
  <button
    v-else
    :class="[
      $style.menuItem,
      customClass ? $style[customClass] : '',
      { [$style.isActive]: itemIsActive },
    ]"
    @click="emit('handleMenuItemClick', item)"
  >
    <NuxtIcon
      :class="$style.itemIcon"
      :name="item.icon"
    />
    <span :class="$style.itemText">
      {{ item.text }}
    </span>
    <span
      v-if="item.pill || item.pill === 0"
      :class="[$style.itemPill, item.pillClass]"
    >
      <NuxtIcon
        v-if="pillIsIcon"
        :name="pillIcon"
      />
      <span v-else>
        {{ item.pill }}
      </span>
    </span>
    <NuxtIcon
      v-if="item.customClass === 'isDisabled'"
      :class="$style.itemArrow"
      name="close"
    />
    <NuxtIcon
      v-else
      :class="$style.itemArrow"
      name="arrows/angle_right"
    />
  </button>
</template>

<style module lang="postcss">
.menuItem {
  @apply w-full text-texts/90 bg-bg-secondary rounded-md flex items-center py-3.5 px-4 gap-3 relative;

  background: rgba(0, 201, 255, 0.1);

  .itemIcon {
    @apply text-lg;
    color: #fff;
  }

  .itemText {
    @apply uppercase font-semibold text-xs;
  }

  .itemPill {
    @apply absolute top-1/2 right-10 -translate-y-1/2 flex items-center justify-center z-40 text-sm font-bold;
  }

  .itemArrow {
    @apply ml-auto;
    color: #fff;
  }

  &.submenuTitle {
    @apply bg-primary/10 text-texts cursor-pointer;

    &:hover {
      @apply opacity-90;
    }

    .itemIcon {
      @apply text-primary;
    }

    .itemText {
      @apply mr-auto font-bold;
    }
  }

  &.isActive {
    border: 1px solid #00c9ff;
    background: rgba(0, 201, 255, 0.1);
    backdrop-filter: blur(10px);

    .itemIcon {
      @apply opacity-100;
      color: #00c9ff;
    }

    .itemText {
      color: #fff;
    }

    .itemArrow {
      color: #00c9ff;
    }
  }

  &.isOpaque {
    @apply bg-bg-secondary/60 text-texts/30;

    .itemArrow {
      @apply text-texts/10;
    }

    &.isActive {
      @apply opacity-50;
    }
  }

  &.isDisabled {
    @apply text-texts/30 cursor-not-allowed;

    .itemArrow {
      @apply text-texts/10;
    }

    &.isActive,
    &:hover {
      @apply opacity-100;
    }
  }

  &.isAlmostHidden {
    @apply bg-bg-secondary/30 text-texts/5 opacity-100 cursor-default;

    .itemArrow {
      @apply text-texts/5;
    }

    .itemPill {
      @apply opacity-10;
    }
  }

  &.isDanger {
    @apply text-error opacity-70;

    .itemIcon {
      @apply text-error;
    }

    .itemText {
      @apply text-error;
    }

    .itemArrow {
      @apply text-error;
    }

    &.isActive {
      @apply opacity-50;
    }
  }

  &.isSuccess {
    @apply text-success opacity-70;

    .itemIcon {
      @apply text-success;
    }

    .itemText {
      @apply text-success;
    }

    .itemArrow {
      @apply text-success;
    }

    &.isActive {
      @apply opacity-50;
    }
  }
}
</style>
